<template>
  <!-- A post written in the new builder (S4_Article): cover full-bleed or
       inset, title, byline with reading time, optional contents list, the
       blocks (post blocks too), tags, a contact card, related posts and
       next / previous. -->
  <article class="s2-post t-band" :data-variant="1">
    <div v-if="post.cover && post.coverStyle === 'full'" class="s2-post__full">
      <Media :media="post.cover" ratio="21 / 9" alt="" />
    </div>
    <div class="t-band__inner s2-post__inner">
      <header class="s2-post__head">
        <p v-if="post.tags.length" class="t-eyebrow">{{ post.tags[0] }}</p>
        <h1 class="t-h1">{{ post.title }}</h1>
        <p v-if="post.excerpt" class="t-lead">{{ post.excerpt }}</p>
        <p class="s2-post__byline t-muted">
          <img v-if="authorPhoto" :src="authorPhoto" alt="" class="s2-post__avatar" />
          <span v-if="post.author"><strong>{{ post.author.name }}</strong><template v-if="post.author.title">, {{ post.author.title }}</template> · </span>
          <span v-if="post.date">{{ post.date }} · </span>
          <span>{{ minutes }} min read</span>
        </p>
      </header>
      <div v-if="post.cover && post.coverStyle === 'inset'" class="s2-post__inset">
        <Media :media="post.cover" ratio="16 / 9" alt="" />
      </div>
      <nav v-if="post.showToc && toc.length > 1" class="s2-post__toc t-card" aria-label="In this post">
        <strong>In this post</strong>
        <ol><li v-for="c in toc" :key="c.id" :class="{sub: c.level === 3}"><a :href="`#${c.id}`" class="t-link">{{ c.text }}</a></li></ol>
      </nav>
      <BlockRenderer :blocks="post.blocks" allow-post :products="products" :newsletter-link="newsletterLink" />
      <ul v-if="post.tags.length" class="s2-post__tags" aria-label="Tags">
        <li v-for="t in post.tags" :key="t"><a :href="`/posts?tag=${encodeURIComponent(t)}`" class="t-badge">{{ t }}</a></li>
      </ul>
      <nav v-if="prev || next" class="s2-post__pn" aria-label="More posts">
        <a v-if="prev" :href="back(prev.path)" class="s2-post__pnlink"><span class="t-eyebrow">← Previous</span><strong>{{ prev.title }}</strong></a>
        <span v-else />
        <a v-if="next" :href="back(next.path)" class="s2-post__pnlink s2-post__pnlink--next"><span class="t-eyebrow">Next →</span><strong>{{ next.title }}</strong></a>
      </nav>
    </div>
    <section v-if="related.length" class="s2-post__related t-band" data-variant="2" aria-label="Related posts">
      <div class="t-band__inner">
        <h2 class="t-h2">You might also like</h2>
        <div class="s2-post__cards mo-stagger">
          <a v-for="r in related" :key="r.path" :href="back(r.path)" class="s2-post__card t-card">
            <Media v-if="r.cover" :media="r.cover" ratio="16 / 10" alt="" />
            <strong>{{ r.title }}</strong>
            <span v-if="r.excerpt" class="t-muted">{{ r.excerpt }}</span>
          </a>
        </div>
      </div>
    </section>
  </article>
</template>

<script setup>
import {computed} from "vue";
import Media from "../shared/Media.vue";
import BlockRenderer from "../blocks/BlockRenderer.vue";
import {contentsOf, mediaOf, normaliseBlocks, readingMinutes} from "@/utils/blocks.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();

const props = defineProps({
  section: {type: Object, required: true},
  products: {type: Object, default: () => ({})},
  newsletterLink: {type: String, default: ""},
});
const post = computed(() => props.section.post || {blocks: [], tags: []});
const related = computed(() => props.section.related || []);
const prev = computed(() => props.section.prev);
const next = computed(() => props.section.next);
const blocks = computed(() => normaliseBlocks({blocks: post.value.blocks}, {allowPost: true}));
const toc = computed(() => contentsOf(blocks.value));
const minutes = computed(() => readingMinutes(blocks.value, post.value.readingMinutes));
const authorPhoto = computed(() => (post.value.author?.photo ? mediaOf(post.value.author.photo).src : ""));
</script>

<style scoped>
.s2-post__full { width: 100%; }
.s2-post__full :deep(img) { border-radius: 0; max-height: 70vh; }
.s2-post__inner { max-width: min(var(--t-max-width), 46rem); }
.s2-post__head { margin-bottom: 1.75rem; }
.s2-post__head .t-h1 { margin: .4rem 0 0; }
.s2-post__byline { display: flex; align-items: center; flex-wrap: wrap; gap: .25rem; margin-top: 1rem; font-size: .92rem; }
.s2-post__avatar { width: 32px; height: 32px; border-radius: 999px; object-fit: cover; margin-right: .4rem; }
.s2-post__inset { margin: 0 0 2rem; }
.s2-post__toc { margin: 0 0 2rem; padding: 1rem 1.25rem; }
.s2-post__toc ol { margin: .5rem 0 0; padding-left: 1.2rem; display: grid; gap: .3rem; }
.s2-post__toc li.sub { margin-left: 1rem; list-style: circle; }
.s2-post__tags { list-style: none; padding: 0; margin: 2rem 0 0; display: flex; flex-wrap: wrap; gap: .5rem; }
.s2-post__tags a { text-decoration: none; }
.s2-post__pn { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-top: 2.5rem; padding-top: 1.5rem; border-top: 1px solid var(--t-line); }
.s2-post__pnlink { display: flex; flex-direction: column; gap: .25rem; color: inherit; text-decoration: none; }
.s2-post__pnlink--next { text-align: right; }
.s2-post__cards { display: grid; gap: var(--t-gap); grid-template-columns: repeat(3, minmax(0, 1fr)); }
@media (max-width: 760px) { .s2-post__cards { grid-template-columns: 1fr; } }
.s2-post__card { display: flex; flex-direction: column; gap: .5rem; color: inherit; text-decoration: none; }
@media (max-width: 640px) { .s2-post__pn { grid-template-columns: 1fr; } .s2-post__pnlink--next { text-align: left; } }
</style>
